import { isNull as _isNull } from 'lodash'
import { TFunction } from 'i18next'

import { UserTypes } from 'Types/user.types.ts'

import { getTranslateMonthNames } from 'Constants/Global.admin.constants.ts'

/**
 * Convert Array list into Object list
 * @param arrList
 */
export const formatArrayToObject = (arrList: Array<{ id: string; value: string }>) => {
  const results: { [key: string]: string } = {}

  for (const obj of arrList) {
    results[obj.id] = obj.value
  }

  return results
}

export const extractInvoiceBillingInformation = (user: UserTypes, t: TFunction) => {
  const { companyName, firstName, lastName, country, city, phone, postalCode, streetName, streetNumber } = user || {}

  return [
    {
      label: t('translation:common.label.company_name'),
      value: companyName,
    },
    {
      label: t('translation:common.label.first_name'),
      value: firstName,
    },
    {
      label: t('translation:common.label.last_name'),
      value: lastName,
    },
    {
      label: t('translation:form.placeholder.address'),
      value: `${streetName}, ${streetNumber}`,
    },
    {
      label: t('translation:form.placeholder.city'),
      value: city,
    },
    {
      label: t('translation:form.placeholder.postal_code'),
      value: postalCode,
    },
    {
      label: t('translation:form.placeholder.country'),
      value: country,
    },
    {
      label: t('translation:form.placeholder.phone'),
      value: phone,
    },
  ]
}

export const formatCommandId = (command: string) => {
  if (_isNull(command) || typeof command !== 'string') {
    return '-'
  }

  return `${command?.slice(0, 3)}...${command?.slice(-6)}`
}

export const extractMinMaxDateFromMonth = (year: string, month: string) => {
  if (typeof year === 'string' && typeof month === 'string') {
    const fullYear = parseInt(year)
    const ordinalMonthNumber = parseInt(month)

    const minDate = new Date(fullYear, ordinalMonthNumber - 1, 1)
    const maxDate = new Date(fullYear, ordinalMonthNumber, 0)

    return {
      minDate,
      maxDate,
    }
  }

  return {
    minDate: null,
    maxDate: null,
  }
}

export const getMonthName = (monthNumber: number) => {
  if (typeof monthNumber === 'number') {
    const date = new Date()
    date.setMonth(monthNumber - 1)

    return date.toLocaleString('en-US', {
      month: 'long',
    })
  }

  console.error('monthNumber must be a number')
  return ''
}

export const generateYearOptions = (limit: number) => {
  const currentYear = new Date().getFullYear()
  const yearRange = []

  for (let i = currentYear - limit; i <= currentYear; i++) {
    yearRange.unshift({ label: i.toString(), value: i.toString() })
  }

  return yearRange
}

export const generateAppYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const yearRange = []

  for (let i = 2023; i <= currentYear; i++) {
    yearRange.unshift({ label: i.toString(), value: i.toString() })
  }

  return yearRange
}

export const generateMonthOptions = (t: TFunction) => {
  const monthNames = getTranslateMonthNames(t)
  return monthNames.map((monthName, index) => ({
    label: monthName,
    value: `${index + 1}`,
  }))
}

export const formatCompactNumber = (number: number) => {
  if (number < 1000) {
    return number
  } else if (number >= 1000 && number < 1_000_000) {
    return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  } else if (number >= 1_000_000 && number < 1_000_000_000) {
    return (number / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  } else if (number >= 1_000_000_000 && number < 1_000_000_000_000) {
    return (number / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  } else if (number >= 1_000_000_000_000 && number < 1_000_000_000_000_000) {
    return (number / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'T'
  }
  return (number / 1_000_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'P'
}

export const formatAbbreviateCurrency = (number: number) => {
  const value = formatCompactNumber(number)
  return `€${value}`
}

export const isDateExpired = (dateString: string): boolean => {
  const currentDate = new Date()
  const targetDate = new Date(dateString)
  return targetDate < currentDate
}

export const formatDateToInputMask = (isoDate: string) => {
  if (!isoDate) return ''

  const date = new Date(isoDate)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

export const formatInputMaskToIsoString = (dateString: string) => {
  if (!dateString) return ''

  const [day, month, year] = dateString.split('/')

  return `${year}-${month}-${day}`
}

export const formatInputMaskToDotIsoString = (dateString: string) => {
  if (!dateString) return ''
  const [day, month, year] = dateString.split('/')
  return `${year}.${month}.${day}`
}

export const isMaskValid = (inputValue: string) => {
  return !inputValue.includes('_')
}

export const convertDateStringToISOString = (dateString: string): string => {
  const [day, month, year] = dateString.split('/').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toISOString()
}

export const isValidDateString = (dateString: string): boolean => {
  const [day, month, year] = dateString.split('/').map(Number)

  if (!day || !month || !year) return false
  if (month < 1 || month > 12) return false
  if (day < 1 || day > 31) return false

  const date = new Date(year, month - 1, day)

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

export const formatMrnUrl = (MRN_NUMBER: string) => {
  if (!MRN_NUMBER) return
  const mrnUrl = `https://ec.europa.eu/taxation_customs/dds2/mrn/mrn_home.jsp?Lang=fr&Expand=true&MRN=${MRN_NUMBER}`
  window.open(mrnUrl, '_blank')
}

export const workingStatusOptions = (t: TFunction) => [
  {
    value: 'APPROVED',
    label: t('translation:common.label.approved'),
  },
  {
    value: 'DECLINED',
    label: t('translation:common.label.declined'),
  },
]

export const checkedStatusOptions = (t: TFunction) => [
  {
    value: 'CHECK',
    label: t('translation:common.label.checked'),
  },
  {
    value: 'UNCHECK',
    label: t('translation:common.label.unchecked'),
  },
]

export const activeStatusOptions = (t: TFunction) => [
  {
    value: 'ENABLE',
    label: t('translation:common.status.enabled'),
  },
  {
    value: 'DISABLE',
    label: t('translation:common.status.disabled'),
  },
]
